import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Loader2, Key, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

interface ExportTabProps {
  exportWallet: (() => Promise<void>) | undefined;
  onPreAction?: () => Promise<void>;
}

export const ExportTab = ({
  exportWallet,
  onPreAction,
}: ExportTabProps) => {
  const [isExporting, setIsExporting] = useState(false);

  const handleExportWallet = async () => {
    if (!exportWallet) {
      toast.error('Export not available');
      return;
    }

    setIsExporting(true);

    // Call pre-action callback (WalletModal uses this to close modal before Turnkey modal appears)
    if (onPreAction) {
      await onPreAction();
    }

    try {
      await exportWallet();
    } catch (error) {
      console.error('[ExportTab] Export error:', error);
      if (error instanceof Error && !error.message.toLowerCase().includes('cancel')) {
        toast.error(error.message || 'Export failed');
      }
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="flex flex-col space-y-4">
      <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
          <div className="space-y-2">
            <p className="text-sm font-medium text-destructive">Security Warning</p>
            <p className="text-xs text-foreground/80 font-normal">
              Your private key gives full control of your wallet. Never share it with anyone.
              If someone gains access to your private key, they can steal all your funds.
            </p>
          </div>
        </div>
      </div>

      <Button
        className="w-full"
        variant="destructive"
        onClick={handleExportWallet}
        disabled={isExporting || !exportWallet}
      >
        {isExporting ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Exporting...
          </>
        ) : (
          <>
            <Key className="h-4 w-4 mr-2" />
            Reveal Private Key
          </>
        )}
      </Button>

      <p className="text-[11px] text-center text-foreground/70 font-normal">
        You can import this key into Phantom, Solflare, or any Solana wallet
      </p>
    </div>
  );
};
